import {
  VoiceAgentConfigurationCallBehaviorRequiredContactInfoType,
  VoiceAgentExternalTransferBehavior,
  VoiceAgentLanguage,
  VoiceAgentSipTransferMode,
  VoiceAgentTransferType,
  type VoiceAgentConfigurationAccountLookupInput,
  type VoiceAgentConfigurationInput,
  type VoiceAgentConfigurationTransferDirectoryEntryInput,
  type VoiceAgentConfigurationTransferInput,
  type VoiceAgentFieldsFragment,
  type VoiceAgentInput,
  type VoiceAgentTransferBehavior,
  type VoiceAgentTransferLineOfBusinessInput
} from '~/api/graphql';
import { normalizeVoiceAccountLookup } from './voice_account_lookup';

export type { VoiceAgentConfigurationAccountLookupInput } from '~/api/graphql';

export type { VoiceAgentTransferLineOfBusinessInput } from '~/api/graphql';

/**
 * The row as loaded, carried on each directory row so a save can tell an edit from a
 * delete plus an add; rows have no id. A symbol, so edits' spreads keep it and JSON drops it.
 */
export const ORIGINAL_ROW: unique symbol = Symbol('originalRow');

export type VoiceAgentSettingsTransferDirectoryEntryInput = Omit<
  VoiceAgentConfigurationTransferDirectoryEntryInput,
  'lineOfBusinessIds'
> & {
  lineOfBusinessIds: string[];
  [ORIGINAL_ROW]?: VoiceAgentSettingsTransferDirectoryEntryInput;
};

// IDs and labels bootstrap unsaved forms. The API supplies canonical classifier metadata on save.
export const DEFAULT_TRANSFER_LINE_OF_BUSINESS_CATALOG: VoiceAgentTransferLineOfBusinessInput[] = [
  {
    id: 'personal',
    label: 'Personal',
    description: '',
    examples: []
  },
  {
    id: 'commercial',
    label: 'Commercial',
    description: '',
    examples: []
  },
  {
    id: 'employee-benefits',
    label: 'Employee Benefits',
    description: '',
    examples: []
  }
];

export const DEFAULT_TRANSFER_LINE_OF_BUSINESS_IDS: ReadonlySet<string> = new Set(
  DEFAULT_TRANSFER_LINE_OF_BUSINESS_CATALOG.map((entry) => entry.id)
);

export const REQUIRED_CONTACT_INFO_LINE_OF_BUSINESS =
  VoiceAgentConfigurationCallBehaviorRequiredContactInfoType.LineOfBusiness;

export function transferDirectoryUsesLineOfBusinessRouting(
  directory: VoiceAgentSettingsTransferDirectoryEntryInput[]
): boolean {
  return directory.some((entry) => (entry.lineOfBusinessIds?.length ?? 0) > 0);
}

export function ensureLineOfBusinessRequiredContactInfo(
  requiredContactInfo: VoiceAgentConfigurationCallBehaviorRequiredContactInfoType[],
  directory: VoiceAgentSettingsTransferDirectoryEntryInput[],
  voice35Enabled = true
): VoiceAgentConfigurationCallBehaviorRequiredContactInfoType[] {
  const withoutLineOfBusiness = requiredContactInfo.filter(
    (contactInfo) => contactInfo !== REQUIRED_CONTACT_INFO_LINE_OF_BUSINESS
  );
  if (voice35Enabled && transferDirectoryUsesLineOfBusinessRouting(directory)) {
    return [...withoutLineOfBusiness, REQUIRED_CONTACT_INFO_LINE_OF_BUSINESS];
  }
  return withoutLineOfBusiness;
}

/** Matches pkg/service/api/cara defaultTransferMessage. */
export const DEFAULT_TRANSFER_MESSAGE =
  'I will be transferring your call now. Please hold for a moment and have a good rest of your day!';

/**
 * Subset of transfer settings read by effective* / isVoice35 helpers.
 * Intentionally partial so legacy rows and unit tests do not need a full
 * VoiceAgentConfigurationTransferInput.
 */
export type VoiceAgentTransferSettingsLike = Partial<
  Pick<
    VoiceAgentConfigurationTransferInput,
    | 'useNewColdTransferConversationFlow'
    | 'warmTransfer'
    | 'transferType'
    | 'externalTransferBehavior'
    | 'externalTransferAllowInternalFallback'
  >
>;

/** Whether the voice agent uses the Voice 3.5 platform (new flow + Voice 3.5 settings). */
export function isVoice35Enabled(transfer: VoiceAgentTransferSettingsLike | undefined): boolean {
  return !!transfer?.useNewColdTransferConversationFlow;
}

/** Valid Voice 3.5 fallback attempt values (total dials Retell may place). */
export const VOICE35_FALLBACK_ATTEMPT_VALUES = [0, 2, 3] as const;

export type Voice35FallbackAttemptValue = (typeof VOICE35_FALLBACK_ATTEMPT_VALUES)[number];

export const VOICE35_FALLBACK_ATTEMPT_OPTIONS: ReadonlyArray<{
  label: string;
  value: Voice35FallbackAttemptValue;
}> = [
  { label: 'No Fallbacks', value: 0 },
  { label: 'One Fallback Call', value: 2 },
  { label: 'Two Fallback Calls', value: 3 }
];

/** Maps a stored fallbackAttempts value to a Voice 3.5 selector option (dead value 1 → No Fallbacks). */
export function voice35FallbackAttemptSelectValue(
  fallbackAttempts: number | null | undefined
): Voice35FallbackAttemptValue {
  if (fallbackAttempts === 2 || fallbackAttempts === 3) {
    return fallbackAttempts;
  }
  return 0;
}

export const EXTERNAL_TRANSFER_BEHAVIOR_OPTIONS: ReadonlyArray<{
  label: string;
  value: VoiceAgentExternalTransferBehavior;
  description: string;
}> = [
  {
    label: 'Best match',
    value: VoiceAgentExternalTransferBehavior.BestMatch,
    description:
      'Consider internal and external numbers together; use AMS carrier data when available.'
  },
  {
    label: 'External number first, internal fallback allowed',
    value: VoiceAgentExternalTransferBehavior.ExternalFirstInternalFallback,
    description:
      'Try external numbers first when Cara routes externally; fall back to internal if needed.'
  },
  {
    label: 'External numbers only',
    value: VoiceAgentExternalTransferBehavior.ExternalOnly,
    description:
      'Only dial external directory numbers; tell the caller no one is available if no match.'
  }
];

/** Resolves external transfer behavior from stored config (unset → EXTERNAL_FIRST_INTERNAL_FALLBACK). */
export function effectiveExternalTransferBehavior(
  transfer: VoiceAgentTransferSettingsLike | undefined
): VoiceAgentExternalTransferBehavior {
  if (transfer?.externalTransferBehavior) {
    return transfer.externalTransferBehavior;
  }
  return VoiceAgentExternalTransferBehavior.ExternalFirstInternalFallback;
}

/** Matches pkg/service/api/cara/transfer_type.go EffectiveVoiceAgentTransferType */
export function effectiveVoiceAgentTransferType(
  transfer: VoiceAgentTransferSettingsLike | undefined
): VoiceAgentTransferType {
  if (!isVoice35Enabled(transfer)) {
    return transfer?.warmTransfer ? VoiceAgentTransferType.Warm : VoiceAgentTransferType.Cold;
  }
  if (transfer?.transferType) {
    return transfer.transferType;
  }
  return transfer?.warmTransfer ? VoiceAgentTransferType.Warm : VoiceAgentTransferType.Cold;
}

/** Whether transfer directory extension fields are editable for the given transfer/SIP config. */
export function isTransferDirectoryExtensionEditable(
  transferType: VoiceAgentTransferType,
  sipTransferMode?: VoiceAgentSipTransferMode | null | ''
): boolean {
  if (
    transferType === VoiceAgentTransferType.Warm ||
    transferType === VoiceAgentTransferType.Agentic
  ) {
    return true;
  }
  // Cold: extensions only supported with SIP INVITE (default when unset or legacy "").
  return sipTransferMode !== VoiceAgentSipTransferMode.Refer;
}

/** Recursively drop GraphQL `__typename` keys from query payloads. Preserves `Date` scalars. */
function stripGraphQlTypenames<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }
  if (value instanceof Date) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => stripGraphQlTypenames(item)) as T;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(record)) {
      if (key === '__typename') {
        continue;
      }
      result[key] = stripGraphQlTypenames(record[key]);
    }
    return result as T;
  }
  return value;
}

/**
 * Transfer block stored in the voice settings form.
 * Intersects generated `VoiceAgentConfigurationTransferInput` with fields the UI reads so tooling
 * stays correct even when local `graphql.tsx` / IDE cache lags the schema.
 */
export type VoiceAgentSettingsTransferState = Omit<
  VoiceAgentConfigurationTransferInput,
  'directory'
> & {
  directory: VoiceAgentSettingsTransferDirectoryEntryInput[];
  lineOfBusinessCatalog?: VoiceAgentTransferLineOfBusinessInput[];
  useNewColdTransferConversationFlow?: boolean | null | undefined;
  transferType?: VoiceAgentTransferType;
  transferMessage?: string;
  transferBehaviorQuote?: VoiceAgentTransferBehavior;
  transferBehaviorFnol?: VoiceAgentTransferBehavior;
  transferBehaviorCoi?: VoiceAgentTransferBehavior;
  transferBehaviorService?: VoiceAgentTransferBehavior;
  transferBehaviorCancellation?: VoiceAgentTransferBehavior;
};

/** Account lookup block stored in the voice settings form. */
export type VoiceAgentSettingsAccountLookupState = VoiceAgentConfigurationAccountLookupInput;

export type VoiceAgentSettingsFormConfiguration = Omit<
  VoiceAgentConfigurationInput,
  'transfer' | 'accountLookup' | 'languages' | 'language'
> & {
  languages: VoiceAgentLanguage[];
  transfer: VoiceAgentSettingsTransferState;
  accountLookup: VoiceAgentSettingsAccountLookupState;
};

/** Canonical form model for voice agent settings (mutation-shaped, UI-safe transfer typing). */
export type VoiceAgentSettingsFormInput = Omit<VoiceAgentInput, 'configuration'> & {
  configuration: VoiceAgentSettingsFormConfiguration;
};

/**
 * Builds `VoiceAgentSettingsFormInput` from Apollo query/mutation data.
 * Keeps a single in-memory shape for the page instead of mixing fragment types with raw spreads.
 */
export function voiceAgentFieldsToVoiceAgentInput(
  agent: Pick<VoiceAgentFieldsFragment, 'name' | 'configuration'>
): VoiceAgentSettingsFormInput {
  const configuration = stripGraphQlTypenames(
    agent.configuration
  ) as VoiceAgentSettingsFormConfiguration;
  const languages = configuration.languages ?? [];
  const voice35Enabled = isVoice35Enabled(configuration.transfer);
  const lineOfBusinessCatalog = voice35Enabled
    ? normalizeTransferLineOfBusinessCatalog(configuration.transfer.lineOfBusinessCatalog)
    : configuration.transfer.lineOfBusinessCatalog;
  const directory = (configuration.transfer.directory ?? []).map((entry) => {
    const row: VoiceAgentSettingsTransferDirectoryEntryInput = {
      ...entry,
      lineOfBusinessIds: entry.lineOfBusinessIds ?? []
    };
    return { ...row, [ORIGINAL_ROW]: row };
  });
  const requiredContactInfo = configuration.callBehavior.requiredContactInfo ?? [];
  return {
    name: agent.name,
    configuration: {
      ...configuration,
      languages: languages.length > 0 ? [...languages] : [VoiceAgentLanguage.EnUs],
      callBehavior: {
        ...configuration.callBehavior,
        requiredContactInfo: ensureLineOfBusinessRequiredContactInfo(
          requiredContactInfo,
          directory,
          voice35Enabled
        )
      },
      transfer: {
        ...configuration.transfer,
        lineOfBusinessCatalog,
        directory
      },
      accountLookup: normalizeVoiceAccountLookup(configuration.accountLookup)
    }
  };
}

export function normalizeTransferLineOfBusinessCatalog(
  catalog: VoiceAgentTransferLineOfBusinessInput[] | null | undefined
): VoiceAgentTransferLineOfBusinessInput[] {
  const customEntries = (catalog ?? []).filter(
    (entry) => !DEFAULT_TRANSFER_LINE_OF_BUSINESS_CATALOG.some((builtIn) => builtIn.id === entry.id)
  );
  return [...DEFAULT_TRANSFER_LINE_OF_BUSINESS_CATALOG, ...customEntries];
}

export function addTransferLineOfBusiness(
  catalog: VoiceAgentTransferLineOfBusinessInput[] | null | undefined,
  lineOfBusiness: VoiceAgentTransferLineOfBusinessInput
): VoiceAgentTransferLineOfBusinessInput[] {
  return [...normalizeTransferLineOfBusinessCatalog(catalog), lineOfBusiness];
}

export function updateTransferLineOfBusiness(
  catalog: VoiceAgentTransferLineOfBusinessInput[] | null | undefined,
  lineOfBusiness: VoiceAgentTransferLineOfBusinessInput
): VoiceAgentTransferLineOfBusinessInput[] {
  return normalizeTransferLineOfBusinessCatalog(catalog).map((entry) =>
    entry.id === lineOfBusiness.id ? lineOfBusiness : entry
  );
}

export function removeTransferLineOfBusiness(
  catalog: VoiceAgentTransferLineOfBusinessInput[] | null | undefined,
  directory: VoiceAgentSettingsTransferDirectoryEntryInput[],
  id: string
): {
  catalog: VoiceAgentTransferLineOfBusinessInput[];
  directory: VoiceAgentSettingsTransferDirectoryEntryInput[];
} {
  return {
    catalog: normalizeTransferLineOfBusinessCatalog(catalog).filter((entry) => entry.id !== id),
    directory: directory.map((entry) => ({
      ...entry,
      lineOfBusinessIds: entry.lineOfBusinessIds.filter(
        (lineOfBusinessId) => lineOfBusinessId !== id
      )
    }))
  };
}

export function countTransferDirectoryEntriesByLineOfBusiness(
  directory: VoiceAgentSettingsTransferDirectoryEntryInput[]
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const entry of directory) {
    for (const id of new Set(entry.lineOfBusinessIds)) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  return counts;
}
